'use client'

import { useEffect, useState } from 'react'
import { setAnalyticsConsent } from '../AnalyticsConsent/AnalyticsConsent'
import { getVisitorRegion } from '@/lib/analytics/region'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import type { CookieBanner as CookieBannerType } from '@/payload-types'
import RichText from '@/ui/components/RichText'

type CookieBannerProps = {
  data?: CookieBannerType | null
}

export const CookieBanner = ({ data }: CookieBannerProps) => {
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    // Only show banner to visitors in Europe (or unknown location) who haven't answered yet.
    // Everyone else is tracked without a banner (see lib/analytics/region.ts).
    // Signed-in members get exactly this treatment too (2026-10-17): logging in is not
    // consent to analytics, so a member in Europe sees the banner like anyone else. What
    // members do on the site is recorded on EA's own server instead (lib/analytics/memberActivity.ts).
    const hasExistingConsent = localStorage.getItem('analytics-consent') !== null
    if (hasExistingConsent) {
      setShowBanner(false)
      return
    }
    let cancelled = false
    getVisitorRegion().then(region => {
      if (!cancelled) setShowBanner(region === 'eu')
    })
    return () => {
      cancelled = true
    }
  }, [])

  const handleAccept = () => {
    setAnalyticsConsent(true)
    setShowBanner(false)
  }

  const handleDecline = () => {
    setAnalyticsConsent(false)
    setShowBanner(false)
  }

  if (!showBanner) return null

  // Fallback to hardcoded content if no CMS data
  const acceptButton = data?.acceptButton || {
    label: 'Accept',
    appearance: 'primary',
    size: 'md',
  }

  const declineButton = data?.declineButton || {
    label: 'Decline',
    appearance: 'secondary',
    size: 'md',
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white bg-opacity-95 backdrop-blur border-t border-gray-200 p-3 sm:p-4 shadow-lg z-50">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-4">
        {data?.message ? (
          <RichText
            data={data.message}
            enableGutter={false}
            className="text-[13px] leading-snug sm:text-sm text-gray-600 prose-p:!mb-0"
          />
        ) : (
          <div className="text-[13px] leading-snug sm:text-sm text-gray-600">
            We use cookies to analyze our traffic and improve your experience. You can choose to
            accept or decline these cookies.
          </div>
        )}
        <div className="flex gap-3 sm:gap-4 w-full sm:w-auto justify-end sm:justify-start">
          <Button
            variant={acceptButton.appearance as any}
            size={acceptButton.size as any}
            onClick={handleAccept}
          >
            <ButtonContent
              showIconLeft={acceptButton.showIconLeft}
              iconLeft={acceptButton.iconLeft}
              label={acceptButton.label}
              showIconRight={acceptButton.showIconRight}
              iconRight={acceptButton.iconRight}
            />
          </Button>
          <Button
            variant={declineButton.appearance as any}
            size={declineButton.size as any}
            onClick={handleDecline}
          >
            <ButtonContent
              showIconLeft={declineButton.showIconLeft}
              iconLeft={declineButton.iconLeft}
              label={declineButton.label}
              showIconRight={declineButton.showIconRight}
              iconRight={declineButton.iconRight}
            />
          </Button>
        </div>
      </div>
    </div>
  )
}
